const {
  checkResponseStatusSuccess,
  checkResponseStatusCanceled,
  checkResponseStatusError,
} = require('../util');

/**
 * WebSocket Service for handling 3DS authentication
 * Extracted from Selector.generate3DSPopUp for reusability
 */
class WebSocketService {
  constructor(fieldsService = null) {
    this.socket = null;
    this.isConnected = false;
    this.fieldsService = fieldsService;
    this.callbacks = {
      onSuccess: null,
      onCancel: null,
      onError: null,
      onClose: null,
    };
  }

  /**
   * Initialize WebSocket connection for 3DS authentication
   * @param {Object} config - Configuration object
   * @param {string} config.wsUrl - WebSocket URL
   * @param {string} config.referenceNumber - Reference number for the transaction
   * @param {string} config.merchantId - Merchant ID
   * @param {Object} config.callbacks - Callback functions
   * @param {Function} config.callbacks.onSuccess - Success callback
   * @param {Function} config.callbacks.onCancel - Cancel callback
   * @param {Function} config.callbacks.onError - Error callback
   * @param {Function} config.callbacks.onClose - Close callback
   * @returns {Promise<boolean>} - Returns true if connection is successful
   */
  connect(config) {
    return new Promise((resolve, reject) => {
      try {
        // Set callbacks first so they're available for error handling
        this.setCallbacks(config.callbacks);
        
        // Validate WebSocket URL
        if (!this.validateWsUrl(config.wsUrl)) {
          const error = {
            status: 'error',
            message: 'Invalid WS_URL',
          };
          this.handleError(error);
          reject(error);
          return;
        }

        if (this.isConnected) {
          this.close();
        }

        // Create WebSocket connection
        this.socket = new WebSocket(config.wsUrl);
        this.setupEventListeners(config.referenceNumber, config.merchantId);

        // Handle connection open
        this.socket.addEventListener('open', () => {
          this.isConnected = true;
          this.sendInitialMessage(config.referenceNumber, config.merchantId);
          resolve(true);
        });

        // Handle connection errors
        this.socket.addEventListener('error', (error) => {
          this.isConnected = false;
          const wsError = {
            status: 'error',
            message: 'WebSocket connection error',
            originalError: error,
          };
          this.handleError(wsError);
          reject(wsError);
        });
      } catch (error) {
        const wsError = {
          status: 'error',
          message: 'Failed to create WebSocket connection',
          originalError: error,
        };
        this.handleError(wsError);
        reject(wsError);
      }
    });
  }

  /**
   * Validate WebSocket URL format
   * @param {string} wsUrl - WebSocket URL to validate
   * @returns {boolean} - Returns true if URL is valid
   */
  validateWsUrl(wsUrl) {
    if (!wsUrl) return false;
    const wsUrlPattern = new RegExp(/^wss?:\/\/(\S+\.)?ottu\.(dev|net)/);
    return wsUrlPattern.test(wsUrl);
  }

  /**
   * Set callback functions
   * @param {Object} callbacks - Callback functions object
   */
  setCallbacks(callbacks) {
    if (callbacks) {
      this.callbacks = {
        ...this.callbacks,
        ...callbacks,
      };
    }
  }

  /**
   * Setup WebSocket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Handle incoming messages
    this.socket.addEventListener('message', (event) => {
      try {
        const response = JSON.parse(event.data);
        this.handleMessage(response);
      } catch (error) {
        const parseError = {
          status: 'error',
          message: 'Failed to parse WebSocket message',
          originalError: error,
        };
        this.handleError(parseError);
      }
    });

    // Handle connection close
    this.socket.addEventListener('close', () => {
      this.isConnected = false;
      this.handleClose();
    });
  }

  /**
   * Send initial message to WebSocket server
   * @param {string} referenceNumber - Reference number for the transaction
   * @param {string} merchantId - Merchant ID
   */
  sendInitialMessage(referenceNumber, merchantId) {
    if (this.socket && this.isConnected) {
      const message = {
        client_type: 'sdk',
        reference_number: referenceNumber,
        merchant_id: merchantId,
      };
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Handle incoming WebSocket messages
   * @param {Object} response - Parsed response from WebSocket
   */
  handleMessage(response) {
    if (checkResponseStatusSuccess(response)) {
      this.handleSuccess(response);
    } else if (checkResponseStatusCanceled(response)) {
      this.handleCancel(response);
    } else if (checkResponseStatusError(response)) {
      this.handleError(response);
    } else {
      const unknownResponse = {
        status: 'error',
        message: 'ws incorrect response',
        originalResponse: response,
      };
      this.handleError(unknownResponse);
    }

    // Close connection after handling response
    if (this.fieldsService) {
      this.fieldsService.disableEnableFields(false);
    }
    this.close();
  }

  /**
   * Handle success response
   * @param {Object} response - Success response object
   */
  handleSuccess(response) {
    if (this.callbacks.onSuccess) {
      this.callbacks.onSuccess(response);
    }
  }

  /**
   * Handle cancel response
   * @param {Object} response - Cancel response object
   */
  handleCancel(response) {
    if (this.callbacks.onCancel) {
      this.callbacks.onCancel(response);
    }
  }

  /**
   * Handle error response
   * @param {Object} response - Error response object
   */
  handleError(response) {
    if (this.callbacks.onError) {
      this.callbacks.onError(response);
    }
  }

  /**
   * Handle connection close
   * @param {Object} response - Response object
   */
  handleClose(response) {
    if (this.callbacks.onClose) {
      this.callbacks.onClose(response);
    }

    // Use FieldsService to enable fields when connection closes
    if (this.fieldsService) {
      this.fieldsService.disableEnableFields(false);
    }
  }

  /**
   * Close WebSocket connection
   */
  close() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Get current WebSocket instance
   * @returns {WebSocket|null} - Returns WebSocket instance or null
   */
  getSocket() {
    return this.socket;
  }
}

module.exports = WebSocketService;
